import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {NavigateFunction, useLocation, useNavigate} from 'react-router';

import {RESET_PASSWORD_EVENTS} from '@/app/auth';
import {useChangePasswordTrackers} from '@/app/main/authentication/pages/ChangePasswordPage/trackers';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {
  PasswordValidationCodes,
  VerifyPasswordState,
} from '@/app/shared-components/VerifyPassword/models/constants';
import {IVerifyPassword} from '@/app/shared-components/VerifyPassword/models/interface';
import {useAmplifyAuth} from '@/hooks';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {AuthErrorNames, ForgotPasswordConfirmInput} from '@/services';
import {isOlderThan, parseToUTC} from '@/utils/dateUtils';
import logError from '@/utils/errors/logError';

const TOAST = {
  EXPIRED_CODE: {
    TITLE: 'Code expired or invalid email',
    DETAILS:
      'This recovery link may have expired, or the email address entered may be incorrect. Please verify your email address or request a new recovery link using the "Forgot password" option.',
  },
  CODE_MISMATCH: {
    TITLE: 'Failed! Incorrect code',
    DETAILS:
      'There was an issue with the verification code. Please request a new recovery link using the "Forgot password" option.',
  },
  INVALID_PASSWORD: {
    TITLE: 'Invalid password',
    DETAILS: `The password you entered doesn't meet our security requirements. Please ensure your password follows the guidelines and try again`,
  },
  LIMIT_EXCEED: {
    TITLE: 'Request Limit Reached',
    DETAILS: `You've reached the limit for this request. Please wait a while before trying again, or contact support if you need assistance`,
  },
  PASSWORD_HISTORY_POLICY_VIOLATION: {
    TITLE: 'Password Reused',
    DETAILS: `Your new password matches one you've used before. Please choose a different password that hasn’t been used previously`,
  },
  USER_NOT_FOUND: {
    TITLE: 'Email Not Found',
    DETAILS: `We couldn't find an account with the provided email address. Please check your email and try again`,
  },
  FAILED: {
    TITLE: 'Confirm reset password problem',
    DETAILS:
      'An error occurred while trying to reset password. Please try again or contact support',
  },
};

const errorMap = {
  [AuthErrorNames.ExpiredCodeException]: TOAST.EXPIRED_CODE,
  [AuthErrorNames.CodeMismatchException]: TOAST.CODE_MISMATCH,
  [AuthErrorNames.InvalidPasswordException]: TOAST.INVALID_PASSWORD,
  [AuthErrorNames.TooManyFailedAttemptsException]: TOAST.LIMIT_EXCEED,
  [AuthErrorNames.TooManyRequestsException]: TOAST.LIMIT_EXCEED,
  [AuthErrorNames.LimitExceededException]: TOAST.LIMIT_EXCEED,
  [AuthErrorNames.PasswordHistoryPolicyViolationException]:
    TOAST.PASSWORD_HISTORY_POLICY_VIOLATION,
  [AuthErrorNames.UserNotConfirmedException]: TOAST.USER_NOT_FOUND,
  [AuthErrorNames.UserNotFoundException]: TOAST.USER_NOT_FOUND,
};
const defaultToast = TOAST.FAILED;

const linkExpiredRoute = '/authentication/password/link-expired';
const offsetMinutesIndia = 330; // UTC+5:30
const linkMinutesExpiration = 60;

type ViewModelContextType = {
  isMobile: boolean;
  email: string;
  newPassword: string;
  confirmNewPassword: string;
  verifyList: IVerifyPassword[];
  isVerify: boolean;
  navigate: NavigateFunction;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setNewPassword: React.Dispatch<React.SetStateAction<string>>;
  setConfirmNewPassword: React.Dispatch<React.SetStateAction<string>>;
  setIsVerify: React.Dispatch<React.SetStateAction<boolean>>;
  handlePressChangePassword: () => void;
  disabledButton: boolean;
  setEmailError?: React.Dispatch<React.SetStateAction<boolean>>;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [PROPERTIES] ----]
  const [verifyList, setVerifyList] = useState<IVerifyPassword[]>([]);
  //#endregion

  //#region [---- [SERVICES] ----]
  const navigate = useNavigate();
  const location = useLocation();
  //#endregion

  //#region [---- [APIS] ----]
  const {confirmForgotPassword} = useAmplifyAuth();
  //#endregion

  //#region [---- [HOOKS] ----]
  const {isMobile} = useWindowSize();
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [isVerify, setIsVerify] = useState<boolean>(false);
  const {t} = useTranslation();
  const {trackChangePassword} = useChangePasswordTrackers();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('code');
  const linkSentAt = queryParams.get('ts');

  const handleQueryParams = useCallback(() => {
    if (location.pathname.endsWith('password/changed')) return;

    if (linkSentAt) {
      const linkDate = parseToUTC(linkSentAt, offsetMinutesIndia);
      if (linkDate) {
        if (isOlderThan(linkDate, linkMinutesExpiration)) {
          navigate(linkExpiredRoute, {
            state: {
              internal_error: 'TimeExpired',
              code: token || '',
              link_sent_at: linkSentAt,
            },
          });
        }
      }
    }
  }, [linkSentAt, location.pathname, navigate, token]);

  useEffect(() => {
    trackChangePassword(RESET_PASSWORD_EVENTS.VIEW_RESET_PASSWORD_SCREEN);
    handleQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVerifyList([
      {
        code: PasswordValidationCodes.MIN_LENGTH,
        description: t('password_component.length'),
        state: VerifyPasswordState.NONE,
        isEnabled: true,
      },
      {
        code: PasswordValidationCodes.NUMBER_UPPER_LOWER,
        description: t('password_component.characters'),
        state: VerifyPasswordState.NONE,
        isEnabled: true,
      },
      {
        code: PasswordValidationCodes.SPECIAL_CHARACTER,
        description: t('password_component.special_character'),
        state: VerifyPasswordState.NONE,
        isEnabled: true,
      },
      {
        code: PasswordValidationCodes.EXACT_MATCH,
        description: t('password_component.match'),
        state: VerifyPasswordState.NONE,
        isEnabled: true,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  //#region [---- [LOGIC] ----]
  const handlePressChangePassword = useCallback(async () => {
    if (newPassword !== confirmNewPassword) return;
    try {
      trackChangePassword(RESET_PASSWORD_EVENTS.SELECT_RESET_CHANGE_PASSWORD);
      const request: ForgotPasswordConfirmInput = {
        username: email,
        password: newPassword,
        confirmationCode: token,
      };

      await confirmForgotPassword(request);
      navigate('/authentication/password/changed');
    } catch (error) {
      logError(error, {
        fileName: 'ChangePasswordPage ViewModelContext',
        functionName: 'handlePressChangePassword',
        extraTags: {
          'user.email': email,
        },
        data: {
          email,
          code: token,
          link_sent_at: linkSentAt,
        },
      });
      const errName = error?.name || '';
      const {TITLE: summary, DETAILS: detail} =
        errorMap[errName] || defaultToast;
      showSnackbar({
        summary: summary,
        message: detail,
      });
      if (errName === AuthErrorNames.ExpiredCodeException) {
        navigate(linkExpiredRoute, {
          state: {
            internal_error: errName,
            email,
            code: token,
            link_sent_at: linkSentAt,
          },
        });
      }
    }
  }, [
    confirmForgotPassword,
    confirmNewPassword,
    email,
    linkSentAt,
    navigate,
    newPassword,
    token,
    trackChangePassword,
  ]);

  const disabledButton = useMemo(() => {
    return (
      !email || !newPassword || !confirmNewPassword || !isVerify || emailError
    );
  }, [email, newPassword, confirmNewPassword, isVerify, emailError]);
  //#endregion

  return (
    <ViewModelContext.Provider
      value={{
        isMobile,
        email,
        newPassword,
        confirmNewPassword,
        verifyList,
        isVerify,
        navigate,
        setEmail,
        setNewPassword,
        setConfirmNewPassword,
        handlePressChangePassword,
        setIsVerify,
        disabledButton,
        setEmailError,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('ChangePasswordPage viewModelContext is undefined');
  }
  return context;
}
